import type { Address, Hex } from 'viem';

import { config } from '../config.ts';
import type { Intent } from '../../../offchain/src/eip712.ts';

/** `waiting`: a limit order resting until the price reaches it */
export type IntentStatus = 'waiting' | 'pending' | 'submitted' | 'succeeded' | 'failed' | 'expired' | 'cancelled';
/**
 * A resting order as the relayer lists it, amounts as decimal strings (margin 18 dec, px oracle units):
 * a limit open (`kind: 'open'`), or a take profit closing `positionId` (`kind: 'tp'`). `isLong` is the
 * side of the position opened or closed.
 */
export type LimitOrder = {
  kind: 'open' | 'tp';
  instrumentId: number;
  isLong: boolean;
  px: string;
  deadline: number;
  margin?: string;
  leverage?: number;
  positionId?: string;
};
export type IntentView = {
  id: Hex;
  action: Intent['action'];
  user: Address;
  status: IntentStatus;
  txHash: Hex | null;
  error: string | null;
  createdAt: number;
  updatedAt: number;
  /** Pending intents ahead of this one in the relayer queue */
  ahead?: number;
  /** Set on limit orders */
  limit?: LimitOrder;
};

export type ApiPosition = {
  id: bigint;
  user: Address;
  instrumentId: number;
  isLong: boolean;
  entryPx: bigint;
  margin: bigint;
  leverage: bigint;
  /** Block of the open, for the position's age */
  openedBlock?: bigint;
};
export type ApiClosed = ApiPosition & {
  outcome: 'closed' | 'liquidated';
  exitPx: bigint;
  credited: bigint;
  fee: bigint;
  blockNumber: bigint;
};
export type ApiClaim = { id: bigint; user: Address; owed: bigint };
export type Transfer = {
  kind: 'deposit' | 'withdraw' | 'coreDeposit' | 'coreWithdraw' | 'cctpDeposit';
  status: 'pending' | 'done' | 'failed';
  /** USD, 18 decimals; 0 while a HyperCore deposit is pending */
  amount: bigint;
  coreWei?: bigint;
  /** 0 for a deposit from another chain still in flight: nothing happened here yet */
  blockNumber: bigint;
  /** The transaction here, or the burn transaction on the source chain while in flight */
  txHash: Hex;
  /** Deposits from another chain: which one */
  source?: string;
  /** Deposits from another chain in flight: when their state last changed, wall clock */
  timeMs?: number;
  error?: string;
};

export type Account = {
  syncedTo: bigint;
  positions: ApiPosition[];
  claims: ApiClaim[];
  history: ApiClosed[];
  /** All-time PAPURR minted on the user's losses (18 dec), and HYPE staking rewards claimed (core wei, 8 dec) */
  papurrEarned: bigint;
  rewardsClaimed: bigint;
};

export class RelayerError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const replacer = (_: string, v: unknown) => (typeof v === 'bigint' ? v.toString() : v);

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${config.relayerUrl}${path}`, init);
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new RelayerError(res.status, body.error ?? `relayer HTTP ${res.status}`);
  return body;
}

type Raw = Record<string, string | number | boolean>;

function position(p: Raw): ApiPosition {
  return {
    id: BigInt(p.id as string),
    user: p.user as Address,
    instrumentId: Number(p.instrumentId),
    isLong: p.isLong as boolean,
    entryPx: BigInt(p.entryPx as string),
    margin: BigInt(p.margin as string),
    leverage: BigInt(p.leverage as string),
    openedBlock: p.openedBlock === undefined ? undefined : BigInt(p.openedBlock as string),
  };
}

/** Account payload (HTTP or WebSocket) to typed values */
export function parseAccount(raw: unknown): Account {
  const r = raw as { syncedTo?: string; positions: Raw[]; claims: Raw[]; history: Raw[]; papurrEarned?: string; rewardsClaimed?: string };
  return {
    syncedTo: BigInt(r.syncedTo ?? 0),
    papurrEarned: BigInt(r.papurrEarned ?? 0),
    rewardsClaimed: BigInt(r.rewardsClaimed ?? 0),
    positions: r.positions.map(position),
    claims: r.claims.map((c) => ({ id: BigInt(c.id as string), user: c.user as Address, owed: BigInt(c.owed as string) })),
    history: r.history.map((h) => ({
      ...position(h),
      outcome: h.outcome as 'closed' | 'liquidated',
      exitPx: BigInt(h.exitPx as string),
      credited: BigInt(h.credited as string),
      fee: BigInt(h.fee as string),
      blockNumber: BigInt(h.blockNumber as string),
    })),
  };
}

export function parseTransfers(raw: unknown): Transfer[] {
  return (raw as Raw[]).map((t) => ({
    kind: t.kind as Transfer['kind'],
    status: t.status as Transfer['status'],
    amount: BigInt(t.amount as string),
    coreWei: t.coreWei !== undefined ? BigInt(t.coreWei as string) : undefined,
    blockNumber: BigInt(t.blockNumber as string),
    txHash: t.txHash as Hex,
    source: t.source as string | undefined,
    timeMs: t.timeMs !== undefined ? Number(t.timeMs) : undefined,
    error: t.error as string | undefined,
  }));
}

export const relayer = {
  /** `type: 'limit'` rests an open until the price reaches its acceptablePx */
  submit(intent: Intent, signature: Hex, type?: 'limit'): Promise<{ id: Hex; status: IntentStatus }> {
    return call('/intents', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ action: intent.action, request: intent.request, signature, type }, replacer),
    });
  },
  /** Drops a pending intent or a resting limit order; 409 once a batcher has it */
  cancel(id: Hex, signature: Hex): Promise<{ id: Hex; status: IntentStatus }> {
    return call(`/intents/${id}/cancel`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ signature }),
    });
  },
  async transfers(user: Address): Promise<Transfer[]> {
    return parseTransfers((await call<{ transfers: Raw[] }>(`/users/${user}/transfers`)).transfers);
  },
  async stats(): Promise<Stats> {
    const r = await call<{ openPositions: number; traders: number; activity24h: Raw; activityAll: Raw }>('/stats');
    return { openPositions: r.openPositions, traders: r.traders, activity24h: parseActivity(r.activity24h), activityAll: parseActivity(r.activityAll) };
  },
  /** Traders by realized PnL over a period, top 100, and `user`'s own row wherever it ranks */
  async leaderboard(period: LeaderPeriod, user?: Address): Promise<Leaderboard> {
    const r = await call<{ rows: Raw[]; me?: Raw }>(`/leaderboard?period=${period}${user ? `&user=${user}` : ''}`);
    return { rows: r.rows.map(parseLeader), me: r.me ? parseLeader(r.me) : undefined };
  },
  intent: (id: Hex) => call<IntentView>(`/intents/${id}`),
  intents: (user: Address, limit = 30) => call<IntentView[]>(`/users/${user}/intents?limit=${limit}`),
  depositAddress: (user: Address) =>
    call<{ depositAddress: Address }>(`/deposit-address/${user}`).then((r) => r.depositAddress),
  /**
   * A USDC burn on another chain (Circle CCTP V2) for the keeper to finalize here; `amount` 18 dec, what was
   * burned; `signature` by the user or a session key over the domain and the hash (eip712 `cctpReportTypedData`)
   */
  reportCctpDeposit: (d: { sourceDomain: number; txHash: Hex; user: Address; amount: bigint; signature: Hex }) =>
    call<{ status: string }>('/cctp/deposits', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(d, replacer),
    }),
  async account(user: Address): Promise<Account> {
    return parseAccount(await call(`/users/${user}/account`));
  },
  /** From the market snapshot: the SessionKeyManager cap on a key's lifetime, seconds */
  maxSessionDuration: () =>
    call<{ globals: { maxSessionDuration: string } }>('/snapshot').then((r) => Number(r.globals.maxSessionDuration)),
  sessionKeyExpiry: (user: Address, key: Address) =>
    call<{ expiry: string }>(`/users/${user}/session-keys/${key}`).then((r) => Number(r.expiry)),
};

export type LeaderPeriod = '24h' | '7d' | '30d' | 'all';
/** One trader's realized result over a period (GET /leaderboard), USD 18 dec */
export type LeaderRow = { rank: number; user: Address; pnl: bigint; volume: bigint; trades: number; wins: number; best: bigint };
export type Leaderboard = { rows: LeaderRow[]; me?: LeaderRow };

function parseLeader(r: Raw): LeaderRow {
  return {
    rank: Number(r.rank),
    user: r.user as Address,
    pnl: BigInt(r.pnl as string),
    volume: BigInt(r.volume as string),
    trades: Number(r.trades),
    wins: Number(r.wins),
    best: BigInt(r.best as string),
  };
}

/** Trading activity over a period (GET /stats), USD amounts 18 dec */
export type Activity = {
  volume: bigint;
  opened: number;
  closed: number;
  wins: number;
  liquidations: number;
  /** Realized PnL of traders net of fees: the house's gross result is minus this, minus the fees */
  tradersPnl: bigint;
  fees: bigint;
};

export type Stats = { openPositions: number; traders: number; activity24h: Activity; activityAll: Activity };

function parseActivity(a: Raw): Activity {
  return {
    volume: BigInt(a.volume as string),
    opened: Number(a.opened),
    closed: Number(a.closed),
    wins: Number(a.wins),
    liquidations: Number(a.liquidations),
    tradersPnl: BigInt(a.tradersPnl as string),
    fees: BigInt(a.fees as string),
  };
}
